import * as React from "react";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleDot,
  FolderPlus,
  Info,
  Loader,
  Plus,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { nodes, templates, templateTypeMapping } from "@/config/nodes";
import { FlowWidget, FlowWidgetProps } from "@/components/widgets/FlowWidget";
import { ProjectContext, ProjectContextType } from "@/context/project";
import { Alert } from "./ui/alert";
import { NodeConfigTable } from "./node-config-table";
import { appSchema, AppContextType } from "@/context/app";

const edgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  data: z.object({
    deploymentUrl: z.boolean().optional(),
    allowCors: z.boolean().optional(),
  }),
});

const appsSchema = z.array(appSchema).superRefine((items, ctx) => {
  if (!items) return;
  const names = new Map<string, number>();

  for (let i = 0; i < items.length; i++) {
    const itemName = items[i].name;
    const normalizedName = itemName.toLowerCase().trim();

    if (normalizedName === "") {
      continue;
    }

    if (names.has(normalizedName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate name: "${itemName}".`,
        path: [i, "name"],
      });
    } else {
      names.set(normalizedName, i);
    }
  }
});

const edgesSchema = z.array(edgeSchema);

const formSchema = z.object({
  name: z.string().min(3, {
    message: "Project Name must be at least 3 characters.",
  }),
  domainName: z
    .string()
    .min(3, {
      message: "Domain Name must be at least 3 characters.",
    })
    .refine(
      (value) => {
        // Reject if contains protocol
        if (/^https?:\/\//i.test(value)) {
          return false;
        }
        // Validate domain format: subdomain.domain.tld
        const domainRegex =
          /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        return domainRegex.test(value);
      },
      {
        message:
          "Please enter a valid domain name (e.g., myapp.com). Do not include http:// or https://",
      },
    ),
  apps: appsSchema,
  edges: edgesSchema,
});

const allMilestones = [
  { id: "details", name: "Project Details" },
  { id: "nodes", name: "Configure Nodes" },
  { id: "connect", name: "Connect Nodes" },
];

export default function NewProjectMilestone() {
  const { projectsMap, setProjectsMap } =
    React.useContext<ProjectContextType>(ProjectContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedApps, setSelectedApps] = useState<AppContextType[]>([]);
  const [requiredApps, setRequiredApps] = useState<AppContextType[]>([]);
  const [creating, setCreating] = useState<boolean>(false);
  const [completedMilestones, setCompletedMilestones] = useState<string[]>([]);

  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      domainName: "",
      apps: [],
      edges: [],
    },
  });

  const processedImportRef = React.useRef(false);

  React.useEffect(() => {
    if (location.state?.importedProject && !processedImportRef.current) {
      const { name, domainName, apps } = location.state.importedProject;
      form.setValue("name", name);
      form.setValue("domainName", domainName);
      setSelectedApps(apps);
      form.setValue("apps", apps);
      setCompletedMilestones(["details"]);
      processedImportRef.current = true;

      // Clear location state to ensure refresh doesn't re-trigger (optional but good practice)
      // navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, form]);

  const addProject = async (payload: unknown) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/project`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        setCreating(false);
        localStorage.removeItem("node");
        if (data?.data?.id) {
          setProjectsMap({
            ...projectsMap,
            [data?.data?.id]: data?.data,
          });
          navigate(`/projects/${data?.data?.id}`);
        }
      } else if (response.status === 400) {
        setCreating(false);
        const responseJson = await response.json();
        form.setError("root.serverError", {
          type: "manual",
          message: responseJson?.error?.message || "Failed to create project",
        });
      } else {
        setCreating(false);
        form.setError("root.serverError", {
          type: "manual",
          message: `Failed to create project (status ${response.status})`,
        });
      }
    } catch {
      setCreating(false);
      form.setError("root.serverError", {
        type: "manual",
        message:
          "Network error or invalid API endpoint. Please verify VITE_PUBLIC_API_ENDPOINT and try again.",
      });
    }
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (values.apps.length > 1 && values.edges.length === 0) {
      form.setError("root.serverError", {
        type: "manual",
        message:
          "You have multiple applications but have not connected them. Please go back and define the connections.",
      });
      setCreating(false);
      return;
    }

    const appsWithAuth = values.apps.filter(
      (app) =>
        app.config.auth &&
        !["auth-service", "database"].includes(app.type as string),
    );

    if (appsWithAuth.length > 0) {
      const authServiceApp = values.apps.find(
        (app) => app.type === "auth-service",
      );

      if (!authServiceApp) {
        form.setError("root.serverError", {
          type: "manual",
          message:
            "Applications with authentication enabled require an 'auth-service' node.",
        });
        setCreating(false);
        return;
      }

      const unconnectedApps = appsWithAuth.filter(
        (webApp) =>
          !values.edges.some(
            (edge) =>
              edge.source === webApp.name &&
              edge.target === authServiceApp.name,
          ),
      );

      if (unconnectedApps.length > 0) {
        const appNames = unconnectedApps.map((app) => app.name).join(", ");
        form.setError("root.serverError", {
          type: "manual",
          message: `The node(s): ${appNames} have authentication enabled but are not connected to an auth-service.`,
        });
        setCreating(false);
        return;
      }
    }

    const appsWithDb = values.apps.filter((app) => app.config.db);

    if (appsWithDb.length > 0) {
      const databaseNodes = values.apps.filter(
        (app) => app.type === "database",
      );

      if (databaseNodes.length === 0) {
        form.setError("root.serverError", {
          type: "manual",
          message:
            "Applications with DB connection enabled require a 'database' node.",
        });
        setCreating(false);
        return;
      }

      const unconnectedDbApps = appsWithDb.filter(
        (serviceApp) =>
          !values.edges.some(
            (edge) =>
              edge.source === serviceApp.name &&
              databaseNodes.some((db) => db.name === edge.target),
          ),
      );

      if (unconnectedDbApps.length > 0) {
        const appNames = unconnectedDbApps.map((app) => app.name).join(", ");
        form.setError("root.serverError", {
          type: "manual",
          message: `The node(s): ${appNames} have DB connection enabled but are not connected to a database.`,
        });
        setCreating(false);
        return;
      }
    }

    const authServiceApp = values.apps.find(
      (app) => app.type === "auth-service",
    );

    if (authServiceApp) {
      const databaseNodes = values.apps.filter(
        (app) => app.type === "database",
      );

      if (databaseNodes.length === 0) {
        form.setError("root.serverError", {
          type: "manual",
          message:
            "The 'auth-service' node requires a connection to a 'database' node.",
        });
        setCreating(false);
        return;
      }

      const isAuthServiceConnectedToDb = values.edges.some(
        (edge) =>
          edge.source === authServiceApp.name &&
          databaseNodes.some((db) => db.name === edge.target),
      );

      if (!isAuthServiceConnectedToDb) {
        form.setError("root.serverError", {
          type: "manual",
          message: "The 'auth-service' node is not connected to a database.",
        });
        setCreating(false);
        return;
      }
    }

    const apps: Record<
      string,
      {
        name: string;
        template?: string;
        config: AppContextType["config"];
      }
    > = {};
    setCreating(true);

    values.apps.forEach((app) => {
      if (app.id && app.template) {
        apps[app.id] = {
          name: app.name,
          template: app.template,
          config: app.config,
        };
      }
    });

    const payload = {
      name: values.name,
      domainName: values.domainName,
      apps,
      edges: values.edges,
    };

    addProject(payload);
  };

  const apps = useWatch({ control: form.control, name: "apps" });
  const edges = useWatch({ control: form.control, name: "edges" });

  const edgesWithIds = React.useMemo(() => {
    const nameToId = (name: string) => {
      const app = (apps as AppContextType[])?.find((a) => a.name === name);
      return app?.id || name;
    };
    return (
      (edges as z.infer<typeof edgesSchema>)?.map((e) => ({
        ...e,
        source: nameToId(e.source),
        target: nameToId(e.target),
      })) || []
    );
  }, [edges, apps]);

  const handleAppAdd = (app: string) => {
    const node = nodes[app as keyof typeof nodes];
    const uniqueId = crypto.randomUUID().slice(0, 8);

    const config: Record<string, unknown> = {};
    if (node?.checkboxOptions) {
      Object.entries(node.checkboxOptions).forEach(([key, opt]) => {
        if (opt) config[key] = opt.defaultValue;
      });
    }
    if (node?.comboboxOptions) {
      Object.entries(node.comboboxOptions).forEach(([key, opt]) => {
        if (opt)
          config[key === "provider" ? "providers" : key] = opt.defaultValue;
      });
    }
    if (node?.inputOptions) {
      Object.entries(node.inputOptions).forEach(([key, opt]) => {
        if (opt) config[key] = opt.defaultValue;
      });
    }

    const currentName = form.getValues("name");
    const newApp: AppContextType = {
      id: `${currentName}-${app}-${uniqueId}`,
      type: app as z.infer<typeof appSchema>["type"],
      name: `${currentName}-${app}-${uniqueId}`,
      config,
      template: node?.templates?.[0]?.value as string,
    };
    const newSelectedApps = [...selectedApps, newApp];
    setSelectedApps(newSelectedApps);
    form.setValue("apps", [...newSelectedApps, ...requiredApps]);
  };

  // Helper to add an app with optional template override
  const addAppWithTemplate = (appKey: string, templateOverride?: string) => {
    const node = nodes[appKey as keyof typeof nodes];
    if (!node) return;
    const uniqueId = crypto.randomUUID().slice(0, 8);

    const config: Record<string, unknown> = {};
    if (node?.checkboxOptions) {
      Object.entries(node.checkboxOptions).forEach(([key, opt]) => {
        if (opt) config[key] = opt.defaultValue;
      });
    }
    if (node?.comboboxOptions) {
      Object.entries(node.comboboxOptions).forEach(([key, opt]) => {
        if (opt)
          config[key === "provider" ? "providers" : key] = opt.defaultValue;
      });
    }
    if (node?.inputOptions) {
      Object.entries(node.inputOptions).forEach(([key, opt]) => {
        if (opt) config[key] = opt.defaultValue;
      });
    }

    const chosenTemplate =
      templateOverride || (node?.templates?.[0]?.value as string);

    const currentName = form.getValues("name");
    const newApp: AppContextType = {
      id: `${currentName}-${appKey}-${uniqueId}`,
      type: appKey as z.infer<typeof appSchema>["type"],
      name: `${currentName}-${appKey}-${uniqueId}`,
      config,
      template: chosenTemplate,
    };

    const newSelectedApps = [...selectedApps, newApp];
    setSelectedApps(newSelectedApps);
    form.setValue("apps", [...newSelectedApps, ...requiredApps]);
  };

  const processedLocalNodeRef = React.useRef(false);
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem("node");
      if (!stored) return;
      if (processedLocalNodeRef.current) return;
      processedLocalNodeRef.current = true;

      // If it matches a template key, map to the node type and add with that template
      if (stored in templates) {
        const nodeType = templateTypeMapping[stored];
        if (nodeType && nodeType in nodes) {
          addAppWithTemplate(nodeType, stored);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  const handleAppRemove = (deletedApp: AppContextType, index: number) => {
    let newSelectedApps = [...selectedApps];
    let newRequiredApps = [...requiredApps];

    if (index < selectedApps.length) {
      newSelectedApps = selectedApps.filter((_, i) => i !== index);
      setSelectedApps(newSelectedApps);
    } else {
      const requiredAppIndex = index - selectedApps.length;
      newRequiredApps = requiredApps.filter((_, i) => i !== requiredAppIndex);
      setRequiredApps(newRequiredApps);

      if (deletedApp.type === "auth-service") {
        newSelectedApps = selectedApps.map((app) => ({
          ...app,
          config: { ...app.config, auth: false },
        }));
        setSelectedApps(newSelectedApps);
      }

      if (deletedApp.type === "database") {
        newSelectedApps = selectedApps.map((app) => ({
          ...app,
          config: { ...app.config, db: false },
        }));
        setSelectedApps(newSelectedApps);
      }
    }
    form.setValue("apps", [...newSelectedApps, ...newRequiredApps]);
  };

  const handleAppEdit = (editedAppIdx: number, editedApp: AppContextType) => {
    if (editedAppIdx < selectedApps.length) {
      const newSelectedApps = selectedApps.map((app, idx) =>
        idx === editedAppIdx ? editedApp : app,
      );
      setSelectedApps(newSelectedApps);
      form.setValue("apps", [...newSelectedApps, ...requiredApps]);
    } else {
      const requiredAppIndex = editedAppIdx - selectedApps.length;
      const newRequiredApps = requiredApps.map((app, idx) =>
        idx === requiredAppIndex ? editedApp : app,
      );
      setRequiredApps(newRequiredApps);
      form.setValue("apps", [...selectedApps, ...newRequiredApps]);
    }
  };

  const appsObject = React.useMemo(() => {
    const result: Record<string, unknown> = {};
    apps?.forEach((app) => {
      if (app.id && app.template) {
        result[app.id] = {
          id: app.id,
          name: app.name,
          type: app.type,
          template: app.template,
          config: app.config || {},
        };
      }
    });
    return result;
  }, [apps]);

  const activeMilestoneId =
    allMilestones[completedMilestones.length]?.id || allMilestones[0].id;

  const handleNext = async () => {
    form.clearErrors("root.serverError");
    console.log("handleNext");
    let fieldsToValidate: ("name" | "domainName" | "apps")[] = [];
    if (activeMilestoneId === "details") {
      fieldsToValidate = ["name", "domainName"];
    } else if (activeMilestoneId === "nodes") {
      if (form.getValues("apps").length === 0) {
        form.setError("apps", {
          type: "manual",
          message: "Please add at least one node to proceed.",
        });
        return;
      }

      // Check if any auth-service nodes have no OAuth providers selected
      const authServiceApps = form
        .getValues("apps")
        .filter((app) => app.type === "auth-service");

      for (const authApp of authServiceApps) {
        const providers = authApp.config?.providers as string[] | undefined;
        if (!providers || providers.length === 0) {
          form.setError("root.serverError", {
            type: "manual",
            message: `The auth-service node "${authApp.name}" requires at least one OAuth provider to be selected.`,
          });
          return;
        }
      }

      fieldsToValidate = ["apps"];
    }

    const isValid = await form.trigger(fieldsToValidate);
    console.log("handleNext isValid", isValid, fieldsToValidate);
    if (isValid) {
      setCompletedMilestones([...completedMilestones, activeMilestoneId]);
    } else {
      console.log(
        "Validation Errors:",
        JSON.stringify(form.formState.errors, null, 2),
      );
      const errors = form.formState.errors;
      const messages: string[] = [];

      if (errors.name?.message)
        messages.push(`Project Name: ${errors.name.message}`);
      if (errors.domainName?.message)
        messages.push(`Domain: ${errors.domainName.message}`);

      if (errors.apps) {
        if (Array.isArray(errors.apps)) {
          errors.apps.forEach((appError, index: number) => {
            if (appError) {
              const nodeName =
                form.getValues(`apps.${index}.name`) || `Node #${index + 1}`;
              Object.entries(appError).forEach(([field, error]) => {
                const err = error as { message?: string };
                if (err?.message) {
                  messages.push(`${nodeName} (${field}): ${err.message}`);
                }
              });
            }
          });
        } else if ((errors.apps as { message?: string }).message) {
          messages.push((errors.apps as { message?: string }).message!);
        }
      }

      form.setError("root.serverError", {
        type: "manual",
        message:
          messages.length > 0
            ? messages.join(". ")
            : "Please fix the validation errors before proceeding.",
      });
    }
  };

  const handleBack = () => {
    form.clearErrors("root.serverError");
    setCompletedMilestones(
      completedMilestones.slice(0, completedMilestones.length - 1),
    );
  };

  const handleInvalidSubmit = () => {
    form.setError("root.serverError", {
      type: "manual",
      message:
        "Please complete Project Details, add Nodes, and connect them before creating.",
    });
  };

  const visibleMilestones = allMilestones.slice(
    0,
    completedMilestones.length + 1,
  );

  const renderMilestoneContent = (milestoneId: string) => {
    switch (milestoneId) {
      case "details":
        return (
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      <span>Project Name</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info size={12} className="cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>This will be your project identifier</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:bg-background active:bg-background"
                        placeholder="Eg., The Fitness App"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="domainName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      <span>Domain Name</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info size={12} className="cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>This will be your project domain name</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="focus:bg-background active:bg-background"
                        placeholder="Eg., myapp.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        );
      case "nodes":
        return (
          <FormField
            control={form.control}
            name="apps"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-3 pt-3">
                      {Object.keys(nodes as typeof nodes)
                        .sort((a, b) => {
                          const nodeA = nodes[a as keyof typeof nodes];
                          const nodeB = nodes[b as keyof typeof nodes];
                          if (nodeA?.disabled && !nodeB?.disabled) return 1;
                          if (!nodeA?.disabled && nodeB?.disabled) return -1;
                          return 0;
                        })
                        .map((app: string) => {
                          const nodeConfig = nodes[app as keyof typeof nodes];
                          const isDisabled = nodeConfig?.disabled;

                          const badge = (
                            <Badge
                              key={app}
                              data-app={app}
                              variant="secondary"
                              className={`inline-flex gap-2 border-2 border-transparent bg-secondary px-2 py-1 text-accent-foreground focus:bg-secondary active:border-input ${
                                isDisabled
                                  ? "cursor-not-allowed opacity-50"
                                  : "cursor-pointer hover:border-primary hover:bg-secondary hover:text-secondary-foreground"
                              }`}
                              {...field}
                              onClick={() => !isDisabled && handleAppAdd(app)}
                            >
                              <span className="flex items-center gap-1">
                                {nodeConfig?.icon} {app}
                              </span>
                              <Plus className="h-4 w-4" />
                            </Badge>
                          );

                          if (isDisabled) {
                            return (
                              <TooltipProvider key={app}>
                                <Tooltip>
                                  <TooltipTrigger>{badge}</TooltipTrigger>
                                  <TooltipContent>
                                    <p>Coming Soon</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }

                          return badge;
                        })}
                    </div>
                    <div>
                      {!!selectedApps.length && (
                        <NodeConfigTable
                          apps={selectedApps
                            .concat(requiredApps)
                            .map((app, idx) => ({
                              id: `${idx}`, // NewProjectMilestone uses index as ID for its local array field
                              name: app.name,
                              template: app.template as string,
                              type: app.type as string,
                              config: app.config || {},
                            }))}
                          errors={form.formState.errors.apps}
                          onUpdateApp={(id: string, updates: unknown) => {
                            const idx = parseInt(id);
                            const app = selectedApps.concat(requiredApps)[idx];
                            handleAppEdit(idx, {
                              ...app,
                              ...(updates as Partial<AppContextType>),
                            });
                          }}
                          onRemoveApp={(id: string) => {
                            const idx = parseInt(id);
                            const app = selectedApps.concat(requiredApps)[idx];
                            handleAppRemove(app, idx);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "connect":
        return (
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="edges"
              render={({ field }) => (
                <div style={{ width: "100%", height: "50vh" }}>
                  <FlowWidget
                    apps={appsObject as FlowWidgetProps["apps"]}
                    edges={edgesWithIds}
                    mode="view-edit"
                    onChange={({ apps: updatedApps, edges: updatedEdges }) => {
                      // Update edges in form state (convert ids -> names for validation)
                      const idToName = (id: string) =>
                        (updatedApps &&
                          updatedApps[id] &&
                          (updatedApps[id] as AppContextType).name) ||
                        id;
                      const nameEdges = (updatedEdges || []).map((e) => ({
                        source: idToName(e.source),
                        target: idToName(e.target),
                        data: e.data || {},
                      }));
                      field.onChange(nameEdges);

                      const appsArray = Object.values(updatedApps || {}).map(
                        (app) => {
                          const typedApp = app as AppContextType;
                          return {
                            id: typedApp.id,
                            name: typedApp.name,
                            type: (typedApp.template
                              ? templateTypeMapping[typedApp.template]
                              : undefined) as AppContextType["type"],
                            template: typedApp.template,
                            config: typedApp.config || {},
                          };
                        },
                      );
                      const currentApps = form.getValues("apps");
                      const nextStr = JSON.stringify(appsArray);
                      const currStr = JSON.stringify(currentApps);
                      if (nextStr !== currStr) {
                        form.setValue("apps", appsArray);
                      }
                    }}
                  />
                </div>
              )}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderMilestoneActions = (id: string) => {
    switch (id) {
      case "details":
        return (
          <div className="flex gap-2">
            <Button type="button" onClick={handleNext} className="gap-2">
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        );
      case "nodes":
        return (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button type="button" onClick={handleNext} className="gap-2">
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        );
      case "connect":
        return (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={creating}
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button
              disabled={creating}
              type="button"
              onClick={() => form.handleSubmit(onSubmit, handleInvalidSubmit)()}
              className="gap-2"
            >
              {creating ? (
                <>
                  Creating... <Loader className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  Create Project <FolderPlus className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  console.log("======> NewProjectMilestone render", form?.formState.errors);

  const isDetailsStep = activeMilestoneId === "details";

  return (
    <main className="w-full p-4 py-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex h-full flex-col"
        >
          <Dialog open={isDetailsStep} onOpenChange={() => {}}>
            <DialogContent
              className="pointer-events-auto sm:max-w-md [&>button]:hidden"
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle>Project Details</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                {/* Re-use the existing content renderer for details */}
                {renderMilestoneContent("details")}
              </div>
              <DialogFooter className="flex w-full justify-between sm:justify-between">
                <Button
                  variant="ghost"
                  onClick={() => {
                    localStorage.removeItem("node");
                    navigate(-1);
                  }}
                  type="button"
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button onClick={handleNext} type="button" className="gap-2">
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Regular Milestone List - Only shown after details are completed */}
          {!isDetailsStep && (
            <div className="space-y-2">
              {form.formState.errors.root?.serverError && (
                <Alert variant={"destructive"} className="mb-4 block">
                  {form.formState.errors.root.serverError.message}
                </Alert>
              )}
              {visibleMilestones
                .slice(0)
                .reverse()
                .map((milestone, index) => {
                  const isActive = activeMilestoneId === milestone.id;
                  const isCompleted = completedMilestones.includes(
                    milestone.id,
                  );

                  // Skip details milestone in the list if we want to hide it completely,
                  // but usually we want to show it as completed in the timeline.
                  // However, 'instead of the current page' implies a distinct view.
                  // Let's keep the standard timeline view for steps 2 & 3.

                  return (
                    <div key={milestone.id} className="relative">
                      <div
                        onClick={(e) => {
                          if (!isCompleted) {
                            e.stopPropagation();
                            e.preventDefault();
                            return;
                          }
                          // Logic to reopen a completed milestone can be added here
                        }}
                        className={`relative h-auto w-full justify-start bg-sidebar px-4 py-4 text-left text-primary ${
                          isActive ? "border border-sidebar-border" : ""
                        } group flex flex-col items-start transition-all duration-300 hover:bg-sidebar`}
                      >
                        {index < visibleMilestones.length - 1 && (
                          <div
                            className={`absolute top-10 left-6.75 z-10 h-[calc(100%-16px)] w-0.25 bg-gray-500`}
                          ></div>
                        )}
                        <div className="relative flex w-full items-center">
                          <span className="relative z-10 mr-3 flex h-6 w-6 items-center justify-center">
                            {isCompleted ? (
                              <CheckCircle2 className="h-5 w-5 rounded-full bg-white text-gray-600" />
                            ) : (
                              <CircleDot
                                strokeWidth={5}
                                className="h-5 w-5 rounded-full bg-white text-amber-600"
                              />
                            )}
                          </span>
                          <div className="flex-grow">
                            <span className="block text-base">
                              {milestone.name}
                            </span>
                          </div>
                          {isActive && (
                            <div className="">
                              {renderMilestoneActions(milestone.id)}
                            </div>
                          )}
                        </div>
                        {isActive && (
                          <div
                            className="w-full pt-4 pl-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {renderMilestoneContent(milestone.id)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </form>
      </Form>
    </main>
  );
}
